import { z } from "zod";
import { isoDate, isoDateTime, optionalEmail, optionalText, phoneInput, text, uuid } from "./common";
import { GENDERS } from "./patients";

/* =================================================================================== campaigns */

/**
 * Who a campaign goes to (version 1). Every filter is optional and they combine with AND.
 * Archived and merged patients are never included; marketing consent is checked per message.
 */
export const campaignAudience = z.object({
  v: z.literal(1).default(1),
  /** Last completed visit at least this many days ago (lapsed patients). */
  lastVisitMinDays: z.number().int().min(1).max(3650).nullish().transform((v) => v ?? null),
  /** Last completed visit within this many days. */
  lastVisitMaxDays: z.number().int().min(1).max(3650).nullish().transform((v) => v ?? null),
  /** Had at least one completed appointment for any of these services. */
  serviceIds: z.array(uuid).max(20).default([]),
  /** Has any of these patient tags. */
  tagIds: z.array(uuid).max(20).default([]),
  /** Visited this branch at least once. */
  branchId: uuid.nullish().transform((v) => v ?? null),
  genders: z.array(z.enum(GENDERS)).max(4).default([]),
  ageMin: z.number().int().min(0).max(120).nullish().transform((v) => v ?? null),
  ageMax: z.number().int().min(0).max(120).nullish().transform((v) => v ?? null),
});
export type CampaignAudience = z.output<typeof campaignAudience>;

export const CAMPAIGN_CHANNELS = ["whatsapp", "email"] as const;
export const CAMPAIGN_STATUSES = ["draft", "scheduled", "sending", "completed", "cancelled"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const campaignInput = z.object({
  name: text(160),
  description: optionalText(2000),
  channel: z.enum(CAMPAIGN_CHANNELS),
  templateId: uuid,
  audience: campaignAudience.prefault({}),
});
export type CampaignInput = z.input<typeof campaignInput>;
export const updateCampaignInput = campaignInput.extend({ version: z.number().int().positive() });

/** Send now (no time) or at a time; the version guards against sending a stale definition. */
export const sendCampaignInput = z.object({ version: z.number().int().positive(), scheduledAt: isoDateTime.nullish().transform((v) => v ?? null) });

export interface AudiencePreview {
  /** Patients matching the filters. */
  matching: number;
  /** Of those: opted in to marketing on this channel and have a number / email. */
  reachable: number;
  sample: { id: string; fullName: string; patientCode: string }[];
}

export interface CampaignStats {
  recipients: number;
  /** Messages not created yet (sending in progress). */
  queued: number;
  pending: number;
  sent: number;
  delivered: number;
  read: number;
  /** Waiting to be sent by hand (no WhatsApp number connected). */
  manual: number;
  failed: number;
  /** Not sent: no consent, no number, quiet-hours limits, … */
  skipped: number;
  /** Recipients who booked an appointment within 30 days of the message. */
  booked: number;
}

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel: (typeof CAMPAIGN_CHANNELS)[number];
  template: { id: string; name: string; status: string; category: string };
  audience: CampaignAudience;
  status: CampaignStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  version: number;
  stats: CampaignStats;
}

/* ======================================================================================= leads */

export const LEAD_STATUSES = ["new", "contacted", "booked", "converted", "lost"] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];
export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = { new: "New", contacted: "Contacted", booked: "Booked", converted: "Became a patient", lost: "Lost" };

const leadFields = {
  fullName: text(160),
  phone: phoneInput,
  email: optionalEmail,
  branchId: uuid.nullish().transform((v) => v ?? null),
  sourceId: uuid.nullish().transform((v) => v ?? null),
  campaignId: uuid.nullish().transform((v) => v ?? null),
  interestedServiceId: uuid.nullish().transform((v) => v ?? null),
  notes: optionalText(4000),
  nextFollowUpOn: isoDate.nullish().transform((v) => v ?? null),
  assignedTo: uuid.nullish().transform((v) => v ?? null),
};
export const leadInput = z.object(leadFields).refine((l) => !!l.phone || !!l.email, { path: ["phone"], message: "Give a phone number or an email" });
export type LeadInput = z.input<typeof leadInput>;
export const updateLeadInput = z
  .object({ ...leadFields, version: z.number().int().positive(), status: z.enum(["new", "contacted", "booked", "lost"]), lostReason: optionalText(500) })
  .refine((l) => !!l.phone || !!l.email, { path: ["phone"], message: "Give a phone number or an email" })
  .refine((l) => l.status !== "lost" || !!l.lostReason, { path: ["lostReason"], message: "Say why the lead was lost" });
export const convertLeadInput = z.object({ version: z.number().int().positive(), patientId: uuid });

export const leadListQuery = z.object({
  status: z.enum([...LEAD_STATUSES, "open", "all"]).default("open"),
  q: z.string().trim().max(100).optional(),
  mine: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export interface Lead {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  branch: { id: string; name: string } | null;
  source: { id: string; name: string } | null;
  campaign: { id: string; name: string } | null;
  interestedService: { id: string; name: string } | null;
  status: LeadStatus;
  lostReason: string | null;
  notes: string | null;
  nextFollowUpOn: string | null;
  assignedTo: { id: string; fullName: string } | null;
  convertedPatient: { id: string; fullName: string; patientCode: string } | null;
  /** An existing patient with the same phone (likely the same person). */
  matchingPatient: { id: string; fullName: string; patientCode: string } | null;
  createdAt: string;
  version: number;
}

/* ===================================================================== treatment conversion */

export const OPPORTUNITY_STAGES = ["open", "considering", "lost"] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];
export const LOST_REASONS = ["cost", "time", "fear", "second_opinion", "went_elsewhere", "not_needed", "unreachable", "other"] as const;
export const LOST_REASON_LABELS: Record<(typeof LOST_REASONS)[number], string> = {
  cost: "Cost", time: "No time", fear: "Anxious", second_opinion: "Wants a second opinion", went_elsewhere: "Went elsewhere",
  not_needed: "Decided not needed", unreachable: "Could not reach", other: "Other",
};
export const ACTIVITY_TYPES = ["note", "call", "whatsapp", "meeting"] as const;

export const opportunityListQuery = z.object({
  view: z.enum(["open", "due", "accepted", "lost"]).default("open"),
  mine: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export const updateOpportunityInput = z
  .object({
    /** Null when no follow-up has been recorded yet for this plan. */
    version: z.number().int().positive().nullable(),
    stage: z.enum(OPPORTUNITY_STAGES),
    lostReason: z.enum(LOST_REASONS).nullish().transform((v) => v ?? null),
    lostNotes: optionalText(1000),
    nextFollowUpOn: isoDate.nullish().transform((v) => v ?? null),
    ownerUserId: uuid.nullish().transform((v) => v ?? null),
  })
  .refine((o) => o.stage !== "lost" || !!o.lostReason, { path: ["lostReason"], message: "Choose why" });

export const opportunityActivityInput = z.object({
  type: z.enum(ACTIVITY_TYPES),
  notes: text(2000),
  nextFollowUpOn: isoDate.nullish().transform((v) => v ?? null),
});

export interface OpportunityActivity {
  id: string;
  type: (typeof ACTIVITY_TYPES)[number] | "stage_change";
  notes: string | null;
  fromStage: string | null;
  toStage: string | null;
  performedBy: string | null;
  occurredAt: string;
}

/** A proposed treatment plan and its follow-up. `status` combines the plan and the follow-up stage. */
export interface Opportunity {
  planId: string;
  opportunityId: string | null;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  doctor: { id: string; fullName: string } | null;
  title: string | null;
  planStatus: string;
  status: "open" | "considering" | "accepted" | "lost";
  totalMinor: number;
  currency: string;
  proposedAt: string | null;
  /** Procedure names; only for staff who may see clinical records. */
  items: string[] | null;
  stage: OpportunityStage;
  lostReason: (typeof LOST_REASONS)[number] | null;
  lostNotes: string | null;
  nextFollowUpOn: string | null;
  owner: { id: string; fullName: string } | null;
  lastActivityAt: string | null;
  version: number | null;
  activities?: OpportunityActivity[];
}
